/** @jsxRuntime automatic */
import {
  Content,
  Header,
  InfoCard,
  Page,
  UserIdentity,
} from '@backstage/core-components';
import {
  IdentityApi,
  configApiRef,
  discoveryApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Button,
  FormControl,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import { FormEvent, useCallback, useState } from 'react';

import { authenticateLocalUser, isStaticAuthUiEnabled } from './localAuth';

function microsoftFailureMessage(error: unknown): string {
  const prefix = 'Microsoft sign-in failed';
  if (!(error instanceof Error)) {
    return `${prefix}: An unknown authentication error occurred.`;
  }

  const safeNames = [
    'Error',
    'AuthenticationError',
    'NotAllowedError',
    'InputError',
    'PopupRejectedError',
    'PopupClosedError',
    'ResponseError',
    'TypeError',
    'NotFoundError',
  ];
  const name = safeNames.includes(error.name) ? error.name : 'Error';
  const message = error.message.slice(0, 4096);
  const aadsts = message.match(/\bAADSTS[0-9]{5,9}\b/)?.[0];
  const categories: [RegExp, string][] = [
    [
      /Auth response is missing cookie nonce/,
      'The callback session cookie is missing.',
    ],
    [/Invalid nonce/, 'The callback session could not be verified.'],
    [/Failed to open auth popup/, 'The sign-in popup could not be opened.'],
    [/popup was closed/, 'The sign-in popup closed before completion.'],
    [
      /Incorrect app origin|Origin .* is not allowed|App origin is invalid/,
      'The sign-in origin was rejected.',
    ],
    [/Missing session cookie/, 'No refresh session cookie is available.'],
    [/invalid_client/, 'Microsoft rejected the application credentials.'],
    [/access_denied/, 'Microsoft denied the sign-in request.'],
    [
      /valid stable user identifier/,
      'Microsoft did not return a supported user identifier.',
    ],
    [
      /Failed to generate new signing key/,
      'The backend could not create a signing key.',
    ],
    [
      /User not found|Failed to sign-in, unable to resolve user identity/,
      'The sign-in identity could not be resolved.',
    ],
    [
      /Auth refresh request failed|Refresh failed/,
      'The authentication session could not be refreshed.',
    ],
  ];
  const category =
    categories.find(([pattern]) => pattern.test(message))?.[1] ??
    'The authentication flow could not complete.';

  // Only fixed text and an allowlisted provider error code leave this boundary.
  return `${prefix} (${name}${aadsts ? `, ${aadsts}` : ''}): ${category}`;
}

type LocalDevelopmentSignInPageProps = {
  onSignInSuccess(identityApi: IdentityApi): void;
  hostedDevelopment?: boolean;
};

export function LocalDevelopmentSignInPage({
  onSignInSuccess,
  hostedDevelopment = false,
}: LocalDevelopmentSignInPageProps) {
  const discoveryApi = useApi(discoveryApiRef);
  const configApi = useApi(configApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [microsoftError, setMicrosoftError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const completeMicrosoftSignIn = useCallback(
    async (options: { optional?: boolean; instantPopup?: boolean }) => {
      const identity = await microsoftAuthApi.getBackstageIdentity(options);
      if (!identity) {
        return;
      }

      const profile = await microsoftAuthApi.getProfile();
      onSignInSuccess(
        UserIdentity.create({
          identity: identity.identity,
          authApi: microsoftAuthApi,
          profile,
        }),
      );
    },
    [microsoftAuthApi, onSignInSuccess],
  );

  const handleMicrosoftSignIn = async () => {
    setMicrosoftError('');
    try {
      await completeMicrosoftSignIn({ instantPopup: true });
    } catch (error) {
      setMicrosoftError(
        isStaticAuthUiEnabled(configApi)
          ? microsoftFailureMessage(error)
          : 'Microsoft sign-in failed',
      );
    }
  };

  const handleLocalSignIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLocalError('');
    setSubmitting(true);

    try {
      const identityApi = await authenticateLocalUser({
        username,
        password,
        discoveryApi,
      });
      setPassword('');
      onSignInSuccess(identityApi);
    } catch {
      setLocalError('Invalid username or password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page themeId="home">
      <Header title={configApi.getString('app.title')} />
      <Content>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <InfoCard
              title="Azure"
              variant="fullHeight"
              actions={
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={handleMicrosoftSignIn}
                >
                  Sign in
                </Button>
              }
            >
              <Typography>Sign in using Azure AD</Typography>
              {microsoftError && (
                <Typography color="error">{microsoftError}</Typography>
              )}
            </InfoCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoCard
              title={
                hostedDevelopment ? 'Development agent' : 'Local development'
              }
              variant="fullHeight"
            >
              <Typography>
                {hostedDevelopment
                  ? 'Sign in with the static DCP development-agent identity.'
                  : 'Sign in with the local AI-agent development identity.'}
              </Typography>
              <form onSubmit={handleLocalSignIn}>
                <FormControl fullWidth>
                  <TextField
                    label="Username"
                    value={username}
                    onChange={event => setUsername(event.target.value)}
                    autoComplete="username"
                    margin="normal"
                    required
                  />
                </FormControl>
                <FormControl fullWidth>
                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={event => setPassword(event.target.value)}
                    autoComplete="current-password"
                    margin="normal"
                    required
                  />
                </FormControl>
                {localError && (
                  <Typography color="error">{localError}</Typography>
                )}
                <Button
                  type="submit"
                  color="primary"
                  variant="outlined"
                  disabled={submitting}
                >
                  {hostedDevelopment
                    ? 'Sign in as development agent'
                    : 'Sign in locally'}
                </Button>
              </form>
            </InfoCard>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
}
